// VipDatePickerStep.jsx
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import moment from "moment-jalaali";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Lock,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  XCircle,
} from "lucide-react";
import { toast } from "react-toastify";

const persianMonths = [
  { id: 1, name: "فروردین", days: 31 },
  { id: 2, name: "اردیبهشت", days: 31 },
  { id: 3, name: "خرداد", days: 31 },
  { id: 4, name: "تیر", days: 31 },
  { id: 5, name: "مرداد", days: 31 },
  { id: 6, name: "شهریور", days: 31 },
  { id: 7, name: "مهر", days: 30 },
  { id: 8, name: "آبان", days: 30 },
  { id: 9, name: "آذر", days: 30 },
  { id: 10, name: "دی", days: 30 },
  { id: 11, name: "بهمن", days: 30 },
  { id: 12, name: "اسفند", days: 29 },
];

const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

const reasonLabelMap = {
  SALON_HOLIDAY: "تعطیلی سالن",
  STAFF_HOLIDAY: "تعطیلی مژه‌کار",
  STAFF_NOT_WORKING: "روز کاری نیست",
  FULLY_BOOKED: "ظرفیت تکمیل",
};

const isLeapYear = (year) => {
  const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
  return breaks.includes(year % 33);
};

const getDaysInMonth = (month, year) => {
  if (!month || month < 1 || month > 12) return 30;
  if (month === 12 && isLeapYear(year)) return 30;
  return persianMonths[month - 1].days;
};

const toDateOnly = (value) => {
  if (!value) return "";
  return String(value).split("T")[0];
};

const VipDatePickerStep = ({
  availabilityData,
  loading,
  error,
  selectedDate,
  onSelectDate,
  onBack,
  onContinue,
  axiosAdmin,
    phone,
  onPhoneChange,
  selectedTime1,
}) => {
  // ----- state for internal date/time -----
  const [internalSelectedDate, setInternalSelectedDate] = useState(selectedDate || null);
  const [selectedTime, setSelectedTime] = useState(selectedTime1 || null);

   // state داخلی برای شماره
  const [internalPhone, setInternalPhone] = useState(phone || "");
  // همگام‌سازی prop
  useEffect(() => {
    setInternalSelectedDate(selectedDate || null);
  }, [selectedDate]);

  // به‌روزرسانی زمان انتخاب‌شده در صورت تغییر پراپ
  useEffect(() => {
    setSelectedTime(selectedTime || null);
  }, [selectedTime]);

  // day detail fetching
  const [dayDetail, setDayDetail] = useState(null);
  const [fetchingDay, setFetchingDay] = useState(false);

  // ref for scrolling to top on mount
  const containerRef = useRef(null);
  // ref for the time selection section (for scrolling after date selection)
  const timeSectionRef = useRef(null);

  const staffId = availabilityData?.selected?.staff?._id;
  const serverToday = availabilityData?.dateNowServer?.today || moment().format("YYYY-MM-DD");
  const serverTime = availabilityData?.dateNowServer?.serverTime;

  const initialMoment = moment(serverToday, "YYYY-MM-DD");

  const [selectedYear, setSelectedYear] = useState(initialMoment.jYear());
  const [selectedMonth, setSelectedMonth] = useState(initialMoment.jMonth() + 1);

  // --- scroll to top on mount ---
  useEffect(() => {
    // small delay to ensure DOM is painted
    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // fallback: scroll window to top
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // --- maps for calendar (already from availability data) ---
  const availableMap = useMemo(() => {
    const map = {};
    const list = availabilityData?.availability?.availableDates || [];
    for (const item of list) {
      map[toDateOnly(item.date)] = item;
    }
    return map;
  }, [availabilityData]);

  const unavailableMap = useMemo(() => {
    const map = {};
    const list = availabilityData?.availability?.unavailableDates || [];
    for (const item of list) {
      map[toDateOnly(item.date)] = item;
    }
    return map;
  }, [availabilityData]);

  const currentMonthName =
    selectedMonth >= 1 && selectedMonth <= 12
      ? persianMonths[selectedMonth - 1].name
      : "نامشخص";

  const changeMonth = (direction) => {
    if (direction === "next") {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear((prev) => prev + 1);
      } else {
        setSelectedMonth((prev) => prev + 1);
      }
    } else {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear((prev) => prev - 1);
      } else {
        setSelectedMonth((prev) => prev - 1);
      }
    }
  };

  const generateCalendarDays = () => {
    if (!selectedMonth || !selectedYear) return [];
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDayOfMonth = moment(
      `${selectedYear}/${selectedMonth}/1`,
      "jYYYY/jM/jD"
    );
    const startDayOfWeek = firstDayOfMonth.day() + 1;
    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const jalaaliDate = moment(
        `${selectedYear}/${selectedMonth}/${day}`,
        "jYYYY/jM/jD"
      );
      const gregorianDate = jalaaliDate.format("YYYY-MM-DD");
      const availableItem = availableMap[gregorianDate];
      const unavailableItem = unavailableMap[gregorianDate];
      days.push({
        day,
        jalaaliDate: jalaaliDate.format("jYYYY/jMM/jDD"),
        gregorianDate,
        isToday: gregorianDate === serverToday,
        isAvailable: Boolean(availableItem),
        isUnavailable: Boolean(unavailableItem),
        isSelected: internalSelectedDate?.date === gregorianDate,
        availableItem,
        unavailableItem,
      });
    }
    return days;
  };

  const calendarDays = generateCalendarDays();

  // ---- handle date selection ----
  const handleSelectDay = (dateObj) => {
    if (!dateObj || !dateObj.isAvailable) return;

    const datePayload = {
      date: dateObj.gregorianDate,
      jalaaliDate: dateObj.jalaaliDate,
      ...dateObj.availableItem,
    };
    setInternalSelectedDate(datePayload);
    setSelectedTime(null);
    if (onSelectDate) onSelectDate(datePayload);

    // scroll to time selection section after the UI updates
    setTimeout(() => {
      if (timeSectionRef.current) {
        timeSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // ---- fetch day availability when date selected ----
  useEffect(() => {
    if (!internalSelectedDate || !staffId || !axiosAdmin) return;
    const fetchDay = async () => {
      setFetchingDay(true);
      setDayDetail(null);
      try {
        const res = await axiosAdmin.get(
          "/api/admin/appointment/day-availability",
          {
            params: {
              staffId,
              date: internalSelectedDate.date,
            },
          }
        );
        setDayDetail(res.data);
      } catch (err) {
        console.error("Error fetching day availability:", err);
        toast.error("خطا در دریافت جزئیات روز");
      } finally {
        setFetchingDay(false);
      }
    };
    fetchDay();
  }, [internalSelectedDate, staffId, axiosAdmin]);

  // ---- logic for time eligibility (past/ booked) ----
  const timeSlots = useMemo(() => {
    if (!dayDetail) return [];
    const { workTimes, bookedTimes } = dayDetail;
    const now = serverTime ? new Date(serverTime) : new Date();
    const selectedDayDate = internalSelectedDate?.date;

    return workTimes.map((time) => {
      const isBooked = bookedTimes.includes(time);
      let isPast = false;
      if (selectedDayDate && time) {
        const timeParts = time.split(":");
        const d = new Date(selectedDayDate + "T" + time + ":00");
        if (d.toDateString() === now.toDateString()) {
          isPast = d.getTime() < now.getTime();
        }
      }
      return {
        time,
        isBooked,
        isPast,
        disabled: isBooked || isPast,
      };
    });
  }, [dayDetail, serverTime, internalSelectedDate]);

  const getUnavailableReasonText = (dateObj) => {
    const reasons = dateObj?.unavailableItem?.reasons || [];
    if (!reasons.length) return "غیرقابل رزرو";

    return reasons
      .map((reason) => reasonLabelMap[reason.code] || reason.message)
      .join("، ");
  };

  // ---- continue action ----
    const handleContinue = () => {
    if (!internalSelectedDate || !selectedTime) return;
 
    onContinue({
      date: internalSelectedDate.date,
      jalaaliDate: internalSelectedDate.jalaaliDate,
      time: selectedTime,
    });
  };


  // ----- render -----
  if (loading) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-9 h-9 text-purple-600 animate-spin" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-2">
            در حال بررسی ظرفیت رزرو
          </h3>
          <p className="text-sm text-slate-500">لطفاً چند لحظه صبر کنید...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-red-700 mb-2">
            خطا در دریافت ظرفیت
          </h3>
          <p className="text-sm text-red-600 leading-7">{error}</p>
          <button
            onClick={onBack}
            className="mt-5 px-5 py-2.5 rounded-xl bg-white border border-red-200 text-red-600 hover:bg-red-100 transition-colors"
          >
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  if (!availabilityData) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="text-center text-slate-500">
          اطلاعاتی برای نمایش وجود ندارد.
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-600 to-fuchsia-600 rounded-2xl p-5 shadow-lg shadow-purple-500/25 overflow-hidden relative">
        <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full bg-white/10" />
        <div className="absolute -bottom-16 -right-16 w-44 h-44 rounded-full bg-white/10" />
        <div className="relative z-10 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-white" />
            <h3 className="text-xl font-bold text-white">
              انتخاب تاریخ رزرو VIP
            </h3>
          </div>
          <p className="text-purple-100 text-sm leading-7">
            روزی که قصد دارید برای خدمت انتخاب‌شده وقت بگیرید را انتخاب کنید.
          </p>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white max-w-2xl mx-auto border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {/* Calendar Header */}
        <div className="bg-slate-900 px-4 md:px-5 py-4 flex items-center justify-between">
          <button
            onClick={() => changeMonth("prev")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>

          <div className="text-center">
            <h3 className="text-lg md:text-xl font-black text-white">
              {currentMonthName} {selectedYear}
            </h3>
            <p className="text-[10px] md:text-xs text-slate-300 mt-1">
              {isLeapYear(selectedYear) ? "سال کبیسه" : "سال عادی"}
            </p>
          </div>

          <button
            onClick={() => changeMonth("next")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Week Days */}
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {weekDays.map((day) => (
            <div
              key={day}
              className="py-2 md:py-3 text-center text-xs md:text-sm font-bold text-slate-600"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid - responsive */}
        <div className="grid grid-cols-7 gap-1 md:gap-2 p-2 md:p-4 bg-white">
          {calendarDays.map((dateObj, index) => {
            if (!dateObj) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }

            const disabled = !dateObj.isAvailable;

            return (
              <button
                key={dateObj.gregorianDate}
                type="button"
                disabled={disabled}
                onClick={() => handleSelectDay(dateObj)}
                title={
                  disabled
                    ? getUnavailableReasonText(dateObj)
                    : `قابل رزرو - ${dateObj.availableItem?.freeTimes?.length || 0} تایم آزاد`
                }
                className={`
                  relative aspect-square rounded-xl md:rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-0.5
                  text-[12px] md:text-base font-black
                  ${
                    dateObj.isSelected
                      ? "border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
                      : dateObj.isAvailable
                      ? "border-green-200 bg-white hover:bg-green-50 hover:border-green-400 hover:shadow-md cursor-pointer"
                      : "border-slate-200 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
                  }
                `}
              >
                {/* today indicator */}
                {dateObj.isToday && (
                  <span
                    className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                      dateObj.isSelected ? "bg-white" : "bg-blue-500"
                    }`}
                  />
                )}

                {/* lock for disabled days */}
                {disabled && (
                  <span className="absolute top-1 left-1">
                    <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-slate-400" />
                  </span>
                )}

                {/* check for available days (desktop only) */}
                {dateObj.isAvailable && !dateObj.isSelected && (
                  <span className="absolute top-1 left-1 hidden md:block">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  </span>
                )}

                <span className="text-base md:text-lg">{dateObj.day}</span>

                {/* reason text only on desktop, not mobile */}
                {!dateObj.isAvailable && (
                  <span className="hidden md:inline text-[9px] md:text-[10px] px-0.5 text-center leading-4 line-clamp-2">
                    {getUnavailableReasonText(dateObj)}
                  </span>
                )}
                {/* show free times count for available days */}
                {dateObj.isAvailable && (
                  <span
                    className={`text-[9px] md:text-[10px] font-medium ${
                      dateObj.isSelected ? "text-purple-100" : "text-green-600"
                    }`}
                  >
                    {dateObj.availableItem?.freeTimes?.length || 0} تایم
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* 👇 New: bottom navigation buttons below the days grid */}
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={() => changeMonth("prev")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
          >
            <ArrowRight className="w-4 h-4" />
            <span>ماه قبل</span>
          </button>

          <button
            onClick={() => changeMonth("next")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium shadow-sm"
          >
            <span>ماه بعد</span>
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Time Selection Section (only if date selected) */}
      {internalSelectedDate && (
        <div ref={timeSectionRef} className="space-y-4">
          {/* Selected date info */}
          <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-2xl p-4">
            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-purple-800">
                تاریخ انتخاب‌شده: {internalSelectedDate.jalaaliDate}
              </p>
            </div>
          </div>

          {/* Time slots */}
          {fetchingDay ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
              <span className="mr-2 text-slate-600">دریافت ساعات...</span>
            </div>
          ) : dayDetail ? (
            <>
              {dayDetail.isAvailable ? (
                <>
                  {timeSlots.filter((s) => !s.disabled).length > 0 ? (
                    <div>
                      <h4 className="text-sm font-bold text-slate-700 mb-3">
                        یک زمان برای رزرو نوبت خود انتخاب کنید
                      </h4>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                        {timeSlots.map((slot) => {
                          const isDisabled = slot.disabled;
                          const isSelected = selectedTime === slot.time;

                          if (isDisabled) {
                            return (
                              <div
                                key={slot.time}
                                className="relative rounded-xl border border-slate-200 bg-slate-100 p-2 text-center opacity-60 cursor-not-allowed select-none"
                              >
                                <span className="text-sm font-semibold text-slate-400 line-through">
                                  {slot.time}
                                </span>
                                {slot.isBooked && (
                                  <span className="absolute top-1 right-1">
                                    <XCircle className="w-3 h-3 text-slate-400" />
                                  </span>
                                )}
                                {slot.isPast && (
                                  <div className="text-[10px] text-red-400 mt-0.5">
                                    گذشت
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <button
                              key={slot.time}
                              type="button"
                              onClick={() => setSelectedTime(slot.time)}
                              className={`
                                relative rounded-xl border-2 p-2 text-center transition-all duration-200
                                ${
                                  isSelected
                                    ? "border-purple-600 bg-purple-50 shadow-md shadow-purple-200"
                                    : "border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm"
                                }
                              `}
                            >
                              <span
                                className={`text-sm font-bold ${
                                  isSelected ? "text-purple-700" : "text-slate-800"
                                }`}
                              >
                                {slot.time}
                              </span>
                              {isSelected && (
                                <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center tex-[10px]">
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                      <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-sm text-amber-800 font-medium">
                        تایم‌های این روز تمام شده. روز دیگری انتخاب کنید.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center">
                  <Lock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-700 font-medium">
                    این روز به دلایل زیر قابل رزرو نیست:
                  </p>
                  <ul className="mt-2 text-xs text-slate-600 list-disc list-inside">
                    {dayDetail.reasons?.map((r, idx) => (
                      <li key={idx}>{r.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-slate-500 py-4">
              اطلاعات ساعتی برای این روز در دسترس نیست.
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="xm:px-5 px-2 py-2.5 rounded-xl xm:text-base text-sm bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
        >
          <ArrowRight className="w-4 h-4" />
          بازگشت
        </button>

        <button
          type="button"
            disabled={!internalSelectedDate || !selectedTime}
            onClick={handleContinue}
          className="xm:px-6 px-3  py-2.5 xm:text-base text-sm rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span className="45xm:block hidden">تأیید رزرو و پرداخت</span>
          <span className="45xm:hidden block"> تأیید و پرداخت</span>
          <span>←</span>
        </button>
      </div>

    </div>
  );
};

export default VipDatePickerStep;
